import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

(async () => {
	// Detect mobile device
	const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
		|| window.innerWidth < 768;

	// Scene setup
	const scene = new THREE.Scene();
	const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 10000);
	const renderer = new THREE.WebGLRenderer({ 
		antialias: !isMobile, // Disable antialiasing on mobile for performance
		powerPreference: isMobile ? 'low-power' : 'high-performance'
	});
	
	renderer.setSize(window.innerWidth, window.innerHeight);
	// Limit pixel ratio on mobile for better performance
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 2 : 3));
	// Enable shadows for realistic lighting
	renderer.shadowMap.enabled = !isMobile; // Disable shadows on mobile
	if (!isMobile) {
		renderer.shadowMap.type = THREE.PCFSoftShadowMap;
	}
	document.getElementById('canvas-container').appendChild(renderer.domElement);

	// Camera position - viewing from above and angle
	camera.position.set(0, 150, 300);
	camera.lookAt(0, 0, 0);

	// Add OrbitControls for interactivity
	const controls = new OrbitControls(camera, renderer.domElement);
	controls.enableDamping = true; // Smooth camera movement
	controls.dampingFactor = 0.05;
	controls.minDistance = 30; // Minimum zoom (close to sun)
	controls.maxDistance = 1000; // Maximum zoom out
	controls.enablePan = true; // Allow panning
	controls.panSpeed = 0.5;
	controls.rotateSpeed = 0.5;
	controls.zoomSpeed = 1.2;

	// Add ambient light (reduced for realistic shadows)
	const ambientLight = new THREE.AmbientLight(0x404040, 0.3);
	scene.add(ambientLight);

	// Add point light at the sun with shadow casting
	const sunLight = new THREE.PointLight(0xffffff, 2.5, 2000);
	sunLight.position.set(0, 0, 0);
	if (!isMobile) {
		sunLight.castShadow = true;
		// Shadow quality settings
		sunLight.shadow.mapSize.width = 2048;
		sunLight.shadow.mapSize.height = 2048;
		sunLight.shadow.camera.near = 10;
		sunLight.shadow.camera.far = 1500;
	}
	scene.add(sunLight);

	// Create starfield
	const starsGeometry = new THREE.BufferGeometry();
	const starsMaterial = new THREE.PointsMaterial({ color: 0xffffff, size: 0.8, transparent: true, opacity: 0.6 });
	const starsVertices = [];

	const starCount = isMobile ? 3000 : 10000; // Reduce stars on mobile
	for (let i = 0; i < starCount; i++) {
		const x = (Math.random() - 0.5) * 4000;
		const y = (Math.random() - 0.5) * 4000;
		const z = (Math.random() - 0.5) * 4000;
		starsVertices.push(x, y, z);
	}

	starsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(starsVertices, 3));
	const stars = new THREE.Points(starsGeometry, starsMaterial);
	scene.add(stars);

	// Planet data (distances scaled for visibility, sizes relatively accurate)
	const planets = [];
	
	function createPlanet(name, size, color, distance, orbitSpeed, hasRings = false) {
		// Planet sphere
		const geometry = new THREE.SphereGeometry(size, 32, 32);
		const material = new THREE.MeshStandardMaterial({ 
			color,
			roughness: 0.7,
			metalness: 0.1,
			emissive: color,
			emissiveIntensity: 0.1 // Reduced for better shadow visibility
		});
		const planet = new THREE.Mesh(geometry, material);
		// Enable shadows
		if (!isMobile) {
			planet.castShadow = true;
			planet.receiveShadow = true;
		}
		
		// Orbit path (visual guide - optional)
		const orbitGeometry = new THREE.BufferGeometry();
		const orbitPoints = [];
		for (let i = 0; i <= 64; i++) {
			const angle = (i / 64) * Math.PI * 2;
			orbitPoints.push(
				Math.cos(angle) * distance,
				0,
				Math.sin(angle) * distance
			);
		}
		orbitGeometry.setAttribute('position', new THREE.Float32BufferAttribute(orbitPoints, 3));
		const orbitMaterial = new THREE.LineBasicMaterial({ color: 0x666666, transparent: true, opacity: 0.5 });
		const orbitLine = new THREE.Line(orbitGeometry, orbitMaterial);
		scene.add(orbitLine);

		// Add rings if needed (Saturn)
		if (hasRings) {
			const ringGeometry = new THREE.RingGeometry(size * 1.5, size * 2.5, 64);
			const ringMaterial = new THREE.MeshStandardMaterial({
				color: 0xc9a97b,
				side: THREE.DoubleSide,
				transparent: true,
				opacity: 0.8,
				emissive: 0xc9a97b,
				emissiveIntensity: 0.1,
				roughness: 0.8
			});
			const rings = new THREE.Mesh(ringGeometry, ringMaterial);
			rings.rotation.x = Math.PI / 2;
			if (!isMobile) {
				rings.castShadow = true;
				rings.receiveShadow = true;
			}
			planet.add(rings);
		}

		const planetData = {
			name,
			mesh: planet,
			distance,
			orbitSpeed,
			angle: Math.random() * Math.PI * 2,
			rotationSpeed: 0.01
		};

		scene.add(planet);
		planets.push(planetData);

		return planetData;
	}

	// Create the Sun
	const sunGeometry = new THREE.SphereGeometry(25, 32, 32);
	const sunMaterial = new THREE.MeshBasicMaterial({ color: 0xfdb813 });
	const sun = new THREE.Mesh(sunGeometry, sunMaterial);
	scene.add(sun);

	// Add sun glow
	const glowGeometry = new THREE.SphereGeometry(28, 32, 32);
	const glowMaterial = new THREE.MeshBasicMaterial({
		color: 0xffaa00,
		transparent: true,
		opacity: 0.4
	});
	const sunGlow = new THREE.Mesh(glowGeometry, glowMaterial);
	scene.add(sunGlow);

	// Create planets (name, size, color, distance from sun, orbit speed)
	createPlanet('Mercury', 4, 0x8c7853, 40, 0.04);
	createPlanet('Venus', 7, 0xffc649, 60, 0.035);
	const earth = createPlanet('Earth', 8, 0x2233ff, 85, 0.03);
	createPlanet('Mars', 5, 0xff4500, 110, 0.024);
	createPlanet('Jupiter', 18, 0xc88b3a, 160, 0.013);
	createPlanet('Saturn', 15, 0xf4e6c3, 210, 0.009, true); // with rings
	createPlanet('Uranus', 10, 0x4fd0e7, 260, 0.006);
	createPlanet('Neptune', 10, 0x4169e1, 310, 0.005);

	// Add Earth's moon
	const moonGeometry = new THREE.SphereGeometry(2, 16, 16);
	const moonMaterial = new THREE.MeshStandardMaterial({ 
		color: 0xcccccc,
		emissive: 0xaaaaaa,
		emissiveIntensity: 0.1,
		roughness: 0.9
	});
	const moon = new THREE.Mesh(moonGeometry, moonMaterial);
	if (!isMobile) {
		moon.castShadow = true;
		moon.receiveShadow = true;
	}
	earth.mesh.add(moon);
	moon.position.set(12, 0, 0);

	// Create Asteroid Belt (between Mars and Jupiter)
	const asteroids = [];
	const asteroidCount = isMobile ? 300 : 800; // Reduce asteroids on mobile
	const asteroidBeltInnerRadius = 120; // Just after Mars (110)
	const asteroidBeltOuterRadius = 150; // Just before Jupiter (160)
	
	for (let i = 0; i < asteroidCount; i++) {
		// Random size for variety
		const size = Math.random() * 0.5 + 0.2;
		
		// Create irregular shapes using dodecahedron for asteroids
		const geometry = new THREE.DodecahedronGeometry(size, 0);
		const material = new THREE.MeshStandardMaterial({
			color: 0x8b7355,
			roughness: 0.9,
			metalness: 0.1
		});
		const asteroid = new THREE.Mesh(geometry, material);
		if (!isMobile) {
			asteroid.castShadow = true;
			asteroid.receiveShadow = true;
		}
		
		// Random position in the belt
		const distance = asteroidBeltInnerRadius + Math.random() * (asteroidBeltOuterRadius - asteroidBeltInnerRadius);
		const angle = Math.random() * Math.PI * 2;
		const verticalOffset = (Math.random() - 0.5) * 8; // Slight vertical variation
		
		asteroid.asteroidData = {
			distance: distance,
			angle: angle,
			orbitSpeed: 0.015 + Math.random() * 0.01, // Random orbit speed
			rotationSpeedX: (Math.random() - 0.5) * 0.02,
			rotationSpeedY: (Math.random() - 0.5) * 0.02,
			rotationSpeedZ: (Math.random() - 0.5) * 0.02,
			verticalOffset: verticalOffset
		};
		
		scene.add(asteroid);
		asteroids.push(asteroid);
	}

	// Text scattering
	const textMeshes = [];

	function scatterThought(text) {
		if (!text.trim()) return;

		// Get input box screen position
		const inputRect = input.getBoundingClientRect();
		const inputCenterX = inputRect.left + inputRect.width / 2;
		const inputCenterY = inputRect.top + inputRect.height / 2;

		// Convert screen coordinates to normalized device coordinates (-1 to +1)
		const ndcX = (inputCenterX / window.innerWidth) * 2 - 1;
		const ndcY = -(inputCenterY / window.innerHeight) * 2 + 1;

		// Create a raycaster to get 3D position from screen coordinates
		const vector = new THREE.Vector3(ndcX, ndcY, 0.5);
		vector.unproject(camera);
		
		const dir = vector.sub(camera.position).normalize();
		const distance = 100; // Distance from camera
		const startPos = camera.position.clone().add(dir.multiplyScalar(distance));

		const letters = text.split('');
		
		letters.forEach((letter, index) => {
			setTimeout(() => {
				// Create 3D text using TextGeometry alternative (sprite-based for simplicity)
				const canvas = document.createElement('canvas');
				const context = canvas.getContext('2d');
				canvas.width = 64;
				canvas.height = 64;
				
				context.font = 'Bold 48px Arial';
				context.fillStyle = 'white';
				context.textAlign = 'center';
				context.textBaseline = 'middle';
				context.fillText(letter, 32, 32);
				
				const texture = new THREE.CanvasTexture(canvas);
				const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
				const sprite = new THREE.Sprite(material);
				
				// All letters start at the exact same position (input box)
				sprite.position.copy(startPos);
				
				sprite.scale.set(10, 10, 1);
				
				// Gentle, graceful velocity - slower and more elegant
				const angle = Math.random() * Math.PI * 2;
				const elevation = (Math.random() - 0.5) * Math.PI / 3; // Less vertical spread
				const speed = Math.random() * 0.5 + 0.3; // Much slower, gentle drift
				
				sprite.velocity = new THREE.Vector3(
					Math.cos(angle) * Math.cos(elevation) * speed,
					Math.sin(elevation) * speed * 0.5, // Even slower vertical movement
					Math.sin(angle) * Math.cos(elevation) * speed
				);
				
				// Add floating wave motion parameters
				sprite.waveAmplitude = Math.random() * 0.3 + 0.1;
				sprite.waveSpeed = Math.random() * 0.02 + 0.01;
				sprite.wavePhase = Math.random() * Math.PI * 2;
				sprite.waveTime = 0;
				
				sprite.rotationSpeed = (Math.random() - 0.5) * 0.02; // Much slower rotation
				sprite.life = 2.5; // Even longer life for graceful fade
				sprite.fadeRate = 0.002; // Slower fade
				
				scene.add(sprite);
				textMeshes.push(sprite);
			}, index * 50); // Slightly slower stagger for elegance
		});

		document.getElementById('thought-input').value = '';
	}

	// Event listeners
	const input = document.getElementById('thought-input');
	const sendButton = document.getElementById('send-button');
	const inputContainer = document.querySelector('.input-container');

	// Rocket launch effect
	function launchRocket() {
		// Only launch if there's text in the input
		if (!input.value.trim()) {
			return;
		}

		const buttonRect = sendButton.getBoundingClientRect();
		const svgRect = sendButton.querySelector('svg').getBoundingClientRect();
		
		// Create flying rocket element (just the icon)
		const rocket = document.createElement('div');
		rocket.className = 'flying-rocket';
		rocket.innerHTML = sendButton.querySelector('svg').outerHTML;
		
		// Position at the SVG icon location
		rocket.style.left = svgRect.left + svgRect.width / 2 - 12 + 'px';
		rocket.style.top = svgRect.top + svgRect.height / 2 - 12 + 'px';
		
		document.body.appendChild(rocket);
		
		// Trigger animation
		requestAnimationFrame(() => {
			rocket.style.animation = 'rocket-liftoff 2.8s cubic-bezier(0.4, 0.0, 0.2, 1) forwards';
		});
		
		// Remove after animation
		setTimeout(() => {
			rocket.remove();
		}, 2800);
	}

	sendButton.addEventListener('click', () => {
		launchRocket();
		scatterThought(input.value);
	});

	input.addEventListener('keypress', (e) => {
		if (e.key === 'Enter') {
			launchRocket();
			scatterThought(input.value);
		}
	});

	// Make input box draggable
	let isDragging = false;
	let currentX;
	let currentY;
	let initialX;
	let initialY;
	let xOffset = 0;
	let yOffset = 0;

	const searchBox = document.querySelector('.search-box');
	const dragHandle = document.querySelector('.drag-handle');

	searchBox.addEventListener('mousedown', dragStart);
	document.addEventListener('mousemove', drag);
	document.addEventListener('mouseup', dragEnd);

	// Touch events for mobile
	searchBox.addEventListener('touchstart', dragStart);
	document.addEventListener('touchmove', drag);
	document.addEventListener('touchend', dragEnd);

	function dragStart(e) {
		// Only drag if clicking on the drag handle or search box background, not the input or button
		if (e.target === input || e.target === sendButton || e.target.closest('#send-button')) {
			return;
		}

		const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
		const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;

		initialX = clientX - xOffset;
		initialY = clientY - yOffset;

		isDragging = true;
		searchBox.style.cursor = 'grabbing';
		if (dragHandle) dragHandle.style.cursor = 'grabbing';
		// Stop the pulse animation when dragging starts
		dragHandle.style.animation = 'none';
	}

	function drag(e) {
		if (isDragging) {
			e.preventDefault();

			const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
			const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;

			currentX = clientX - initialX;
			currentY = clientY - initialY;

			xOffset = currentX;
			yOffset = currentY;

			setTranslate(currentX, currentY, inputContainer);
		}
	}

	function dragEnd(e) {
		initialX = currentX;
		initialY = currentY;

		isDragging = false;
		searchBox.style.cursor = 'grab';
		if (dragHandle) dragHandle.style.cursor = 'grab';
	}

	function setTranslate(xPos, yPos, el) {
		el.style.transform = `translate(calc(-50% + ${xPos}px), calc(-50% + ${yPos}px))`;
	}

	// Handle window resize
	window.addEventListener('resize', () => {
		camera.aspect = window.innerWidth / window.innerHeight;
		camera.updateProjectionMatrix();
		renderer.setSize(window.innerWidth, window.innerHeight);
	});

	// Animation loop
	function animate() {
		requestAnimationFrame(animate);

		// Update controls for damping
		controls.update();

		// Rotate sun
		sun.rotation.y += 0.001;
		sunGlow.rotation.y -= 0.001;

		// Update planet positions and rotations
		planets.forEach(planet => {
			planet.angle += planet.orbitSpeed * 0.01;
			planet.mesh.position.x = Math.cos(planet.angle) * planet.distance;
			planet.mesh.position.z = Math.sin(planet.angle) * planet.distance;
			planet.mesh.rotation.y += planet.rotationSpeed;
		});

		// Update asteroid belt positions and rotations
		asteroids.forEach(asteroid => {
			const data = asteroid.asteroidData;
			data.angle += data.orbitSpeed * 0.01;
			
			// Update position
			asteroid.position.x = Math.cos(data.angle) * data.distance;
			asteroid.position.y = data.verticalOffset;
			asteroid.position.z = Math.sin(data.angle) * data.distance;
			
			// Tumbling rotation on all axes
			asteroid.rotation.x += data.rotationSpeedX;
			asteroid.rotation.y += data.rotationSpeedY;
			asteroid.rotation.z += data.rotationSpeedZ;
		});

		// Rotate stars slowly
		stars.rotation.y += 0.0001;

		// Update text particles
		for (let i = textMeshes.length - 1; i >= 0; i--) {
			const sprite = textMeshes[i];
			
			// Add gentle wave motion for floating effect
			sprite.waveTime += sprite.waveSpeed;
			const waveY = Math.sin(sprite.waveTime + sprite.wavePhase) * sprite.waveAmplitude;
			const waveX = Math.cos(sprite.waveTime * 0.7 + sprite.wavePhase) * sprite.waveAmplitude * 0.5;
			
			// Apply velocity with wave motion
			sprite.position.x += sprite.velocity.x + waveX;
			sprite.position.y += sprite.velocity.y + waveY;
			sprite.position.z += sprite.velocity.z;
			
			// Gentle rotation
			sprite.material.rotation += sprite.rotationSpeed;
			
			// Fade out gracefully
			sprite.life -= sprite.fadeRate;
			sprite.material.opacity = Math.max(0, sprite.life);

			if (sprite.life <= 0) {
				scene.remove(sprite);
				sprite.material.dispose();
				sprite.material.map.dispose();
				textMeshes.splice(i, 1);
			}
		}

		renderer.render(scene, camera);
	}

	animate();
})();
